import { apiGet, apiPost, apiPutRequest } from 'helpers/ApiHelper';
import * as types from './types';
import { showFailure } from 'actions/notificationActions';
import { toDate } from 'helpers/Formatter';

export const submitPayload = ({
  selectedUnit,
  application_package_id,
  selectedApplicant,
  additional_applicants = [],
  wait_unit,
  collect_first_months_with_deposit,
  create_prorated_charge,
  lease_start_date,
  lease_end_date,
  lease_term,
  early_move_in_date,
  monthly_rent,
  deposit_amount,
  key_deposit,
  deposit_required_for_lease,
  incentives,
  deposits,
  lease_items,
  // https://github.com/yuhuinc/yuhu/blob/main/front_end/src/components/landlord_app/application_management/additional_information/Fees.js#L169
  fee_templates,
  // 'Save Application' => isDraft is `true`, `Send Invitations` => isDraft is `false`
  is_draft,
  services,
  utilities,
}) => {
  let birthday = null;

  if (selectedApplicant.birthday) {
    birthday =
      typeof selectedApplicant.birthday === 'string'
        ? selectedApplicant.birthday
        : selectedApplicant.birthday.format('YYYY-MM-DD');
  }

  const leadData = {
    email: selectedApplicant.email,
    first_name: selectedApplicant.first_name,
    last_name: selectedApplicant.last_name,
    birthday,
    phone_number: selectedApplicant.phone_number,
    contact_methods: selectedApplicant.contact_methods,
    erp_ids: selectedApplicant.erp_ids,
  };

  let desired_lease_items = [];
  let actual_lease_items = [];

  lease_items.forEach((lease_item) => {
    if (lease_item.assign_item_type_later) {
      desired_lease_items.push(lease_item);
    } else {
      actual_lease_items.push(lease_item);
    }
  });

  let payload = {
    property_id: selectedUnit.property_id,
    delay_unit_assignment: wait_unit,
    unit: wait_unit ? null : selectedUnit.id,
    unit_type_id: wait_unit ? selectedUnit.id : null,
    collect_first_months_with_deposit: collect_first_months_with_deposit,
    create_prorated_charge,
    lease_start_date: lease_start_date?.format('YYYY-MM-DD'),
    lease_end_date: lease_end_date?.format('YYYY-MM-DD'),
    early_move_in_date: early_move_in_date?.format('YYYY-MM-DD'),
    lease_term,
    monthly_rent,
    deposit_amount,
    key_deposit,
    deposit_required_for_lease,
    application_package_id: application_package_id,

    // Each `deposit_payments` object is of the form:
    // { amount: <Float>, date: <Date>, paid_in_person: <Boolean> }
    deposit_payments: deposits,

    coapplicants_in_application: additional_applicants.length > 0,
    fee_template_ids: fee_templates.map((fee_template) => fee_template.id),

    // each of these objects in `additionalApplicants` array requires:
    // in all cases (new and existing leads):
    //   * `email`, `is_guarantor`
    // for new leads:
    //   * `first_name`, `last_name`, `phone_number`, `dob`, `yearly_income`
    additional_applicants,

    // Incentives created from an incentive template will always have an internal code,
    // whereas one-time incentives will not.
    incentive_template_ids: incentives
      .filter((incentive) => !!incentive.internal_code)
      .map((incentive) => incentive.id),
    one_time_incentives: incentives.filter((incentive) => !incentive.internal_code),

    // each object in `lease_items` array must contain keys:
    //   `amount`, `deposit_amount`, `key_deposit_amount`, `start_date`, `item_type`,
    //   `end_date` OR `duration` (integer for months, or `monthly` for every month),
    // the following keys are optional:
    //   `item_name`, `ancillary_item_id` (used to map to existing AncillaryItem records on the property).
    //
    desired_lease_items,
    lease_items: actual_lease_items,
    is_draft,
    services_and_utilities: {
      services,
      utilities,
    },
    ...leadData,
    ...(!!selectedApplicant.lead_card ? { lead_card_id: selectedApplicant.lead_card.id } : {}),
  };

  return payload;
};

export function resetApplicationStatuses() {
  return (dispatch) => {
    return dispatch({
      type: types.RESET_APPLICATION_STATUSES,
    });
  };
}

export function showCreateApplicationSuccess(isDraft = false, newApplicationId) {
  return {
    type: types.CREATE_APPLICATION_SUCCESS,
    payload: { isDraft, newApplicationId },
  };
}

export function showCreateApplicationError(isDraft = false) {
  return {
    type: types.CREATE_APPLICATION_FAILURE,
    payload: { isDraft },
  };
}

export function createApplication(data) {
  const payload = submitPayload(data);
  const isDraft = data?.is_draft || false;
  return (dispatch) => {
    return dispatch({
      type: types.CREATE_APPLICATION,
      payload: apiPost(`/landlords/application_management/applications`, payload)
        .then((response) => {
          const newApplicationId = response?.application?.id;
          dispatch(showCreateApplicationSuccess(isDraft, newApplicationId));
          return response;
        })
        .catch((error) => {
          dispatch(showCreateApplicationError(isDraft));
        }),
    });
  };
}

export function getSitesAndProperties({ onlyReviewableProperties = false } = {}) {
  return apiGet(
    `/landlords/application_management/applications/sites_and_properties?only_reviewable_properties=${
      onlyReviewableProperties ? 'true' : 'false'
    }`
  );
}

export function getAvailableUnits({ siteId, propertyId, leaseStartDate, unitFormType }) {
  let route = 'available_units';
  if (unitFormType === 'wait_unit_form') {
    route = 'available_unit_types';
  }

  return apiGet(`/landlords/application_management/applications/${route}`, {
    site_id: siteId,
    property_id: propertyId,
    lease_start_date: leaseStartDate?.format ? leaseStartDate.format('YYYY-MM-DD') : leaseStartDate,
  });
}

export function searchApplicants({ query, propertyId }) {
  return apiGet('/landlords/application_management/leads', {
    query,
    property_id: propertyId,
  });
}

export function findDuplicates(email, selectedApplicantId) {
  return apiGet('/landlords/application_management/leads/find_duplicate', {
    email,
    lead_id: selectedApplicantId,
  });
}

// TODO - replace with searchIncentiveTemplates, based on unit, lease term, etc.
export function searchIncentives({ unitId, leaseTerm, query }) {
  return apiGet(`/landlords/application_management/units/${unitId}/incentive_templates`, {
    query,
    lease_term: leaseTerm,
  });
}

export function getLead(leadId) {
  return apiGet(`/landlords/application_management/leads/${leadId}`);
}

export function getUnit(unitId) {
  return apiGet(`/landlords/application_management/units/${unitId}`);
}

export function getUnitType(unitTypeId, siteId, propertyId) {
  return apiGet(`/landlords/application_management/unit_types/${unitTypeId}`, {
    site_id: siteId,
    property_id: propertyId,
  });
}

export function prorateRent(data) {
  return apiPost('/landlords/application_management/applications/prorate_rent', data);
}

export function getApplicantDeposits(applicantId) {
  return apiGet(`/landlords/application_management/lead_cards/${applicantId}/deposits`);
}

export function getLeaseTermPricings(leaseStartDate, unitId) {
  return apiGet(`/landlords/application_management/units/${unitId}/lease_term_pricings`, {
    lease_start_date: toDate(leaseStartDate),
  });
}

export function searchFeeTemplates({ unit_ids, query, yuhu_module, status } = {}) {
  return apiGet('/landlords/fees/fee_templates', {
    query,
    unit_ids,
    yuhu_module,
    status,
  });
}

export function getApplicationNotes(applicationId) {
  return apiGet(
    `/landlords/application_management/applications/${applicationId}/notes?page_size=100`
  );
}

export function createApplicationNote(applicationId, data) {
  return apiPost(`/landlords/application_management/applications/${applicationId}/notes`, data);
}

export function inviteAllApplicants(applicationId) {
  return (dispatch) => {
    return apiPost(
      `/landlords/application_management/applications/${applicationId}/applicants/invite_all`
    ).catch((error) => {
      dispatch(showFailure(error));
    });
  };
}

export function showAssignAgentSuccess() {
  return {
    type: types.ASSIGN_AGENT_SUCCESS,
  };
}

export function showAssignAgentFailure() {
  return {
    type: types.ASSIGN_AGENT_FAILURE,
  };
}

export function assignAgent(applicationId, agentId) {
  return (dispatch) => {
    return dispatch({
      type: types.ASSIGN_AGENT,
      payload: apiPutRequest(
        `/landlords/application_management/applications/${applicationId}/assign_agent`,
        {
          agent_id: agentId,
        }
      )
        .then((response) => {
          dispatch(showAssignAgentSuccess());
          return response;
        })
        .catch((_) => dispatch(showAssignAgentFailure())),
    });
  };
}

export function getApplicationPackages(unitId) {
  return apiGet('/landlords/application_packages/packages', {
    application_status: 'published',
    unit_id: unitId,
  });
}

export function getTenancyLaw(unitId, leaseStartDate) {
  return apiGet(`/landlords/application_management/units/${unitId}/tenancy_law`, {
    lease_start_date: toDate(leaseStartDate),
  });
}
